<template>
  <transition name="localisation-switch-layer-animate-top">
    <form
      class="localisation-switch-layer"
      v-if="isOpen"
      method="get"
      action=""
      ref="localisationSwitchLayer"
      v-outside-click:[true]="close"
    >
      <div class="localisation-switch-layer__head">
        {{ $t('localisation.layer.headline') }}
        <button
          class="localisation-switch-layer__close"
          :aria-label="$t('global.close')"
          ref="closeBtnElm"
          @click="close"
          type="button"
        />
      </div>
      <div class="localisation-switch-layer__content">
        <select-vue
          class="localisation-switch-layer__country-select"
          v-model="model.country"
          :options="getCountryOptions()"
          :initial-value="props.selectedCountry"
        />
        <select-vue
          v-if="getLanguageOptions().length > 1"
          class="localisation-switch-layer__language-select"
          v-model="model.language"
          :options="getLanguageOptions()"
          :initial-value="model.language"
        />
        <input
          type="hidden"
          name="geo"
          :value="getSubmitValue(model.country)"
        >
        <input
          type="hidden"
          name="selectedCountry"
          :value="model.country"
        >
        <input
          v-if="getLanguageOptions().length > 1"
          type="hidden"
          name="lang"
          :value="getSubmitValue(model.language)"
        >
        <button
          class="localisation-switch-layer__btn | button button--primary"
          :class="{'button--loading': isLoading}"
          type="submit"
        >
          {{ $t('localisation.layer.button.save') }}
        </button>
      </div>
    </form>
  </transition>
</template>

<script setup lang="ts">
  import {ref, onMounted, nextTick, onBeforeUnmount} from 'vue';
  import pigeon from '../../utilities/js/pigeon/pigeon';
  import SelectVue from '../select/select.vue';
  import {t} from '@/bps/utilities/js/translations/translations';
  import jsGlobalConfig from '../../globals/jsGlobalConfig';

  const globalConfig = jsGlobalConfig();

  const props = defineProps<{
    selectedCountry: string;
  }>();

  const isOpen = ref(false);
  const isLoading = ref(false);
  const localisationSwitchLayer = ref<HTMLElement | null>(null);
  const closeBtnElm = ref<HTMLElement | null>(null);
  let lastFocusedElm: HTMLElement = null;

  const model = ref({
    country: '',
    language: '',
  });

  const open = () => {
    isOpen.value = true;
    pigeon.publish('pageoverlay:show', 'page-overlay--visible-transparent');

    nextTick(() => {
      if (localisationSwitchLayer.value) {
        handleFocus(true);
      }
    });
  };

  const handleFocus = (modalActive: boolean) => {
    if (modalActive) {
      lastFocusedElm = document.activeElement as HTMLElement;
      nextTick(() => {
        localisationSwitchLayer.value.setAttribute('tabindex', '0');
        closeBtnElm.value.focus();
        localisationSwitchLayer.value.addEventListener('transitionend', () => {
          if (isOpen.value && localisationSwitchLayer.value && !localisationSwitchLayer.value.contains(document.activeElement)) {
            closeBtnElm.value.focus();
          }
        });
      });
    }
    else {
      if (lastFocusedElm) {
        nextTick(() => lastFocusedElm.focus());
      }
    }
  };

  const close = () => {
    if (isOpen.value) {
      isOpen.value = false;
      handleFocus(false);
      pigeon.publish('pageoverlay:hide', 'page-overlay--visible-transparent');
    }
  };

  const getSubmitValue = (val: string) => {
    return t(`localisation.layer.value.country.${val}`).toString();
  };

  const getCountryOptions = () => {
    const options = ['de', 'ch', 'at', 'global'];

    return options.map((key) => {
      return {
        label: t(`localisation.layer.country.${key}`),
        value: key,
        icon: `<img src="${globalConfig.getAssetPath()}/frontend-core/staticImages/country-${key}.svg" alt="${key}" class="localisation-button__flag" />`
      }
    });
  };

  const getLanguageOptions = () => {
    const options = ['de'];

    return options.map((key) => {
      return {
        label: t(`localisation.layer.language.${key}`),
        value: key
      }
    });
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
    }
  };

  onMounted(() => {
    pigeon.subscribe('localisationSwitch:open', open);
    window.addEventListener('keydown', handleKeydown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeydown);
  });
</script>
